import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { api } from "./api";
import { trackAddToCart, trackRemoveFromCart } from "./analytics";

const CartContext = createContext(null);

const GUEST_KEY = "mps_guest_cart_id_v1";

function ensureGuestId() {
  let gid = localStorage.getItem(GUEST_KEY);
  if (!gid) {
    gid = "g-" + (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
    localStorage.setItem(GUEST_KEY, gid);
  }
  return gid;
}

function guestHeader() {
  return { headers: { "X-Guest-Cart-Id": ensureGuestId() } };
}

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [issues, setIssues] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [tempRequired, setTempRequired] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const busy = useRef(false);

  const applyResponse = useCallback((data) => {
    setItems(data.items || []);
    setIssues(data.issues || []);
    setSubtotal(data.subtotal_cents || 0);
    setTempRequired(!!data.temp_controlled_required);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/cart", guestHeader());
      applyResponse(data);
    } catch (err) {
      // Cart fetch is best-effort; a stale/expired guest cart is not a hard failure.
      if (process.env.NODE_ENV !== "production") console.warn("cart.refresh failed:", err);
    } finally {
      setLoaded(true);
    }
  }, [applyResponse]);

  useEffect(() => { refresh(); }, [refresh]);

  const add = useCallback(async (product, quantity = 1, opts = {}) => {
    if (busy.current) return;
    busy.current = true;
    try {
      const { data } = await api.post(
        "/cart/items",
        { product_id: product.id, quantity },
        guestHeader(),
      );
      applyResponse(data);
      // Ecommerce dataLayer — fires after successful server persistence so
      // we never emit for a failed add.
      try { trackAddToCart(product, quantity); } catch (_) {}
      if (opts.openDrawer !== false) setDrawerOpen(true);
      return data;
    } finally {
      busy.current = false;
    }
  }, [applyResponse]);

  const update = useCallback(async (product_id, quantity) => {
    const { data } = await api.patch(`/cart/items/${product_id}`, { quantity }, guestHeader());
    applyResponse(data);
  }, [applyResponse]);

  const remove = useCallback(async (product_id) => {
    // Snapshot the line item BEFORE the mutation so we can emit an
    // accurate `remove_from_cart` event even when the server response
    // strips the row from the returned cart.
    const removed = items.find((i) => i.product_id === product_id);
    const { data } = await api.delete(`/cart/items/${product_id}`, guestHeader());
    applyResponse(data);
    if (removed) {
      try { trackRemoveFromCart(removed, removed.quantity); } catch (_) {}
    }
  }, [applyResponse, items]);

  const clear = useCallback(async () => {
    const { data } = await api.delete("/cart", guestHeader());
    applyResponse(data);
  }, [applyResponse]);

  const mergeOnAuth = useCallback(async () => {
    // Called by AuthProvider after login/register. Server merges guest cart → user cart.
    const gid = localStorage.getItem(GUEST_KEY);
    try {
      const { data } = await api.post("/cart/merge", { guest_cart_id: gid || null });
      applyResponse(data);
      // Rotate the guest id so future logouts don't collide with prior merges.
      localStorage.removeItem(GUEST_KEY);
      ensureGuestId();
    } catch (err) {
      // Merge failure is non-fatal — user can still checkout with their user cart.
      if (process.env.NODE_ENV !== "production") console.warn("cart.mergeOnAuth failed:", err);
    }
  }, [applyResponse]);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = useMemo(
    () => ({
      items, issues, subtotal, tempRequired, count, loaded,
      add, update, remove, clear, refresh, mergeOnAuth,
      drawerOpen, setDrawerOpen,
    }),
    [items, issues, subtotal, tempRequired, count, loaded, drawerOpen,
     add, update, remove, clear, refresh, mergeOnAuth],
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
